import { Children, cloneElement, useContext, useEffect, useState } from 'react';

import { useURLQuery } from '../../../Common/Hook/urlQuery';
import { useHRReportsGetQuery } from '../../../Common/Queries/hrReportsQueries';

import { isValidDate, isValidID } from '../../../utils';

import { AuthContext, HR_REPORT_TABLE_COLUMNS_ID } from '../../../constants';

import { getFromLocalStorage } from '../../../services/localStorage';

export const validateFilterObject = (filterObject) => {
  const keyArray = ['status', 'searchValue', 'page', 'order', 'orderBy'];
  return keyArray.every((key) => {
    if (key === keyArray[0]) {
      return filterObject[key] !== undefined && Array.isArray(filterObject[key]);
    }
    if (key === keyArray[1]) {
      return filterObject[key] !== undefined && typeof filterObject[key] === 'string';
    }
    if (key === keyArray[2]) {
      return (
        filterObject[key] !== undefined &&
        typeof filterObject[key] === 'number' &&
        filterObject[key] >= 0
      );
    }
    if (key === keyArray[3]) {
      return (
        filterObject[key] === '' || filterObject[key] === 'asc' || filterObject[key] === 'desc'
      );
    }
    return filterObject[key] === '' || filterObject[key] === 'name';
  });
};

const initialValues = {
  status: [],
  uploaded_application: [],
  scheduled_interview: [],
  positions: [],
  canceled_interview: [],
  order: '',
  orderBy: '',
  searchValue: '',
  page: 0,
  fromDate: '',
  toDate: '',
};

export const HRReportListContainer = ({ filters: filterValues, children }) => {
  const urlQuery = useURLQuery();
  const { userInfo } = useContext(AuthContext);
  const initialSearch = urlQuery.get('search');
  const initialStatus = urlQuery.get('status');
  const initialFilterValues = getFromLocalStorage(`${userInfo?.sub}-hrReport`);
  const filterValuesObj = initialFilterValues && JSON.parse(initialFilterValues);
  const initialFromDate = isValidDate(urlQuery.get('from')) && urlQuery.get('from');
  const initialToDate = isValidDate(urlQuery.get('to')) && urlQuery.get('to');

  const initialPage = isValidID(urlQuery.get('page')) && urlQuery.get('page');

  const [hrAllData, setHRAllData] = useState([]);
  const [hrenabled, setHREnabled] = useState(false);
  const [hrpage, setHRPage] = useState(1);

  const initialFilterValuesToApply = {
    ...initialValues,
    searchValue: initialSearch || '',
    order: '',
    orderBy: '',
    page: initialPage ? Number(initialPage - 1) : 0,
    fromDate: initialFromDate || filterValues.fromDate,
    toDate: initialToDate || filterValues.toDate,
    ...(filterValuesObj && validateFilterObject(filterValuesObj)
      ? { status: initialStatus ? initialStatus.split(',') : filterValuesObj.status }
      : { status: initialStatus ? initialStatus.split(',') : [] }),
  };
  const [filters, updateFilters] = useState(initialFilterValuesToApply);

  useEffect(() => {
    updateFilters((prev) => ({
      ...prev,
      fromDate: filterValues.fromDate,
      toDate: filterValues.toDate,
    }));
  }, [filterValues]);

  const filterObj = (obj) => {
    const value = Object.keys(obj).reduce((acc, key) => {
      if (
        HR_REPORT_TABLE_COLUMNS_ID.find((v) => v === key) &&
        ((obj[key]?.min && obj[key]?.min !== '') || (obj[key]?.max && obj[key]?.max !== ''))
      )
        return { ...acc, [key]: obj[key] };
      return acc;
    }, {});
    return value;
  };
  const interviewers = useHRReportsGetQuery({
    ...filterObj(filters),
    page: Number(filters.page + 1),
    status: filters.status.join(','),
    sort: filters.order.toUpperCase(),
    orderBy: filters.orderBy,
    'dateRange.fromDate': filters.fromDate,
    'dateRange.toDate': filters.toDate,
    'dateRange.includeDate': true,
    q: filters.searchValue,
    limit: 20,
  });

  const hrReportsAllData = useHRReportsGetQuery(
    {
      status: filters.status.join(','),
      sort: filters.order.toUpperCase(),
      orderBy: filters.orderBy,
      'dateRange.fromDate': filters.fromDate,
      'dateRange.toDate': filters.toDate,
      'dateRange.includeDate': true,
      q: filters.searchValue,
      limit: 100,
      page: hrpage,
    },
    { enabled: hrenabled }
  );

  useEffect(() => {
    if (hrReportsAllData.isSuccess && hrenabled) {
      const transformedData =
        hrReportsAllData.data?.data?.map(({ user, ...rest }) => ({
          userId: user.id,
          name: user.name,
          ...rest,
        })) || [];

      setHRAllData([...hrAllData, ...transformedData]);
    }

    if (hrReportsAllData.data?.meta?.total > hrpage * 100) {
      setHRPage((prev) => prev + 1);
    }
  }, [hrReportsAllData.isSuccess]);

  useEffect(() => {
    if (hrAllData.length > 0) {
      setHRAllData([]);
      setHRPage(1);
    }
  }, [filters]);

  const handleOnFilterChange = (updatedFilters) => {
    updateFilters((prev) => ({ ...prev, ...updatedFilters }));
  };

  const child = Children.only(children);

  return cloneElement(child, {
    interviewers,
    filterHandler: handleOnFilterChange,
    filters,
    enabled: hrenabled,
    setEnabled: setHREnabled,
    allData: setHRAllData,
    hrReportsAllData,
  });
};
